/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitStatus, VehicleStatus, VehicleType } from 'src/graphql/generated/graphql.generated';
import {
  OutOfServiceEventSummaryRecord,
  OutOfServiceEventSummaryProps,
} from 'src/store/generated/out-of-service-event-summary-record.generated';
import {
  CoordinatesRecord,
  CoordinatesProps,
} from 'src/store/generated/coordinates-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ExternalSystemUnitProps {
  id: string;
  name: string;
  abbreviation: string;
  unitId: string;
  vehicleId: string;
  regionId: string;
  companyId: string;
  unitStatus: UnitStatus;
  outOfServiceEvents: List<OutOfServiceEventSummaryProps>;
  vehicleType: VehicleType;
  registrationNumber: string;
  vehicleStatus: VehicleStatus;
  vehicleStatusDescription: string;
  currentCardNumber: string | null;
  lastKnownLocationName: string | null;
  lastKnownCoordinates: CoordinatesProps | null;
  lastQualifyingPositionReportTime: Date | null;
  baseId: string | null;
  modelName: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ExternalSystemUnitRecord extends Record<ExternalSystemUnitProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  unitId: null as any,
  vehicleId: null as any,
  regionId: null as any,
  companyId: null as any,
  unitStatus: null as any,
  outOfServiceEvents: null as any,
  vehicleType: null as any,
  registrationNumber: null as any,
  vehicleStatus: null as any,
  vehicleStatusDescription: null as any,
  currentCardNumber: null as any,
  lastKnownLocationName: null as any,
  lastKnownCoordinates: null as any,
  lastQualifyingPositionReportTime: null as any,
  baseId: null as any,
  modelName: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    unitId: string;
    vehicleId: string;
    regionId: string;
    companyId: string;
    unitStatus: UnitStatus;
    outOfServiceEvents: any;
    vehicleType: VehicleType;
    registrationNumber: string;
    vehicleStatus: VehicleStatus;
    vehicleStatusDescription: string;
    currentCardNumber: string | null;
    lastKnownLocationName: string | null;
    lastKnownCoordinates: any;
    lastQualifyingPositionReportTime: string | number | Date | null;
    baseId: string | null;
    modelName: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ExternalSystemUnitRecord {
    if (json == null) {
      throw new Error('ExternalSystemUnitRecord.fromJS: parameter cannot be null');
    }
    return new ExternalSystemUnitRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ExternalSystemUnitJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'ExternalSystemUnitJson.name'),
      abbreviation: getFromOrThrow(
        json,
        (j) => j.abbreviation,
        'ExternalSystemUnitJson.abbreviation',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'ExternalSystemUnitJson.unitId',
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'ExternalSystemUnitJson.vehicleId',
      ),
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'ExternalSystemUnitJson.regionId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ExternalSystemUnitJson.companyId',
      ),
      unitStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitStatus', UnitStatus.Available),
        'ExternalSystemUnitJson.unitStatus',
      ),
      outOfServiceEvents: List<OutOfServiceEventSummaryRecord>(
        getFromOrThrow(
          json,
          (j) => j.outOfServiceEvents,
          'ExternalSystemUnitJson.outOfServiceEvents',
        ).map((o: any) => new OutOfServiceEventSummaryRecord(o)),
      ),
      vehicleType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleType', VehicleType.Unspecified),
        'ExternalSystemUnitJson.vehicleType',
      ),
      registrationNumber: getFromOrThrow(
        json,
        (j) => j.registrationNumber,
        'ExternalSystemUnitJson.registrationNumber',
      ),
      vehicleStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleStatus', VehicleStatus.AtWaypoint),
        'ExternalSystemUnitJson.vehicleStatus',
      ),
      vehicleStatusDescription: getFromOrThrow(
        json,
        (j) => j.vehicleStatusDescription,
        'ExternalSystemUnitJson.vehicleStatusDescription',
      ),
      currentCardNumber: json.currentCardNumber,
      lastKnownLocationName: json.lastKnownLocationName,
      lastKnownCoordinates:
        json.lastKnownCoordinates != null ? new CoordinatesRecord(json.lastKnownCoordinates) : null,
      lastQualifyingPositionReportTime:
        json.lastQualifyingPositionReportTime != null
          ? new Date(json.lastQualifyingPositionReportTime)
          : null,
      baseId: json.baseId,
      modelName: json.modelName,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    unitId: string;
    vehicleId: string;
    regionId: string;
    companyId: string;
    unitStatus: UnitStatus;
    outOfServiceEvents: any;
    vehicleType: VehicleType;
    registrationNumber: string;
    vehicleStatus: VehicleStatus;
    vehicleStatusDescription: string;
    currentCardNumber: string | null;
    lastKnownLocationName: string | null;
    lastKnownCoordinates: any;
    lastQualifyingPositionReportTime: string | number | Date | null;
    baseId: string | null;
    modelName: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ExternalSystemUnitRecord, Error[]] {
    if (json == null) {
      throw new Error('ExternalSystemUnitRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ExternalSystemUnitRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ExternalSystemUnitJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'ExternalSystemUnitJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'ExternalSystemUnitJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'ExternalSystemUnitJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'ExternalSystemUnitJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'ExternalSystemUnitJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ExternalSystemUnitJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitStatus', UnitStatus.Available),
            'ExternalSystemUnitJson.unitStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceEvents: tryOrDefault(
        () =>
          List<OutOfServiceEventSummaryRecord>(
            getFromOrThrow(
              json,
              (j) => j.outOfServiceEvents,
              'ExternalSystemUnitJson.outOfServiceEvents',
            ).map((o: any) => new OutOfServiceEventSummaryRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleType', VehicleType.Unspecified),
            'ExternalSystemUnitJson.vehicleType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      registrationNumber: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.registrationNumber,
            'ExternalSystemUnitJson.registrationNumber',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleStatus', VehicleStatus.AtWaypoint),
            'ExternalSystemUnitJson.vehicleStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleStatusDescription: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.vehicleStatusDescription,
            'ExternalSystemUnitJson.vehicleStatusDescription',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentCardNumber: tryOrDefault(
        () => json.currentCardNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastKnownLocationName: tryOrDefault(
        () => json.lastKnownLocationName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastKnownCoordinates: tryOrDefault(
        () =>
          json.lastKnownCoordinates != null
            ? new CoordinatesRecord(json.lastKnownCoordinates)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastQualifyingPositionReportTime: tryOrDefault(
        () =>
          json.lastQualifyingPositionReportTime != null
            ? new Date(json.lastQualifyingPositionReportTime)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () => json.baseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      modelName: tryOrDefault(
        () => json.modelName,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ExternalSystemUnitProps) {
    super(props);
    if (props == null) {
      throw new Error('ExternalSystemUnitRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ExternalSystemUnitViewModelBase implements ExternalSystemUnitProps {
  protected readonly _props: ExternalSystemUnitProps;
  private _json?: ExternalSystemUnitProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get unitStatus(): UnitStatus {
    return this._props.unitStatus;
  }
  get outOfServiceEvents(): List<OutOfServiceEventSummaryProps> {
    return this._props.outOfServiceEvents;
  }
  get vehicleType(): VehicleType {
    return this._props.vehicleType;
  }
  get registrationNumber(): string {
    return this._props.registrationNumber;
  }
  get vehicleStatus(): VehicleStatus {
    return this._props.vehicleStatus;
  }
  get vehicleStatusDescription(): string {
    return this._props.vehicleStatusDescription;
  }
  get currentCardNumber(): string | null {
    return this._props.currentCardNumber;
  }
  get lastKnownLocationName(): string | null {
    return this._props.lastKnownLocationName;
  }
  get lastKnownCoordinates(): CoordinatesProps | null {
    return this._props.lastKnownCoordinates;
  }
  get lastQualifyingPositionReportTime(): Date | null {
    return this._props.lastQualifyingPositionReportTime;
  }
  get baseId(): string | null {
    return this._props.baseId;
  }
  get modelName(): string | null {
    return this._props.modelName;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAvailable() {
    return this._props.unitStatus === UnitStatus.Available;
  }
  public get isCommitted() {
    return this._props.unitStatus === UnitStatus.Committed;
  }
  public get isOnStandby() {
    return this._props.unitStatus === UnitStatus.OnStandby;
  }
  public get isOutOfService() {
    return this._props.unitStatus === UnitStatus.OutOfService;
  }
  public get isPartiallyAvailable() {
    return this._props.unitStatus === UnitStatus.PartiallyAvailable;
  }
  public get isAir() {
    return this._props.vehicleType === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.vehicleType === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.vehicleType === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.vehicleType === VehicleType.RotorWing;
  }
  public get isUnspecified() {
    return this._props.vehicleType === VehicleType.Unspecified;
  }
  public get isAtWaypoint() {
    return this._props.vehicleStatus === VehicleStatus.AtWaypoint;
  }
  public get isEnRoute() {
    return this._props.vehicleStatus === VehicleStatus.EnRoute;
  }

  public isAnyUnitStatus(...unitStatus: UnitStatus[]) {
    return unitStatus.includes(this._props.unitStatus);
  }
  public isAnyVehicleType(...vehicleType: VehicleType[]) {
    return vehicleType.includes(this._props.vehicleType);
  }
  public isAnyVehicleStatus(...vehicleStatus: VehicleStatus[]) {
    return vehicleStatus.includes(this._props.vehicleStatus);
  }

  constructor(props: ExternalSystemUnitProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        unitId: this._props.unitId,
        vehicleId: this._props.vehicleId,
        regionId: this._props.regionId,
        companyId: this._props.companyId,
        unitStatus: this._props.unitStatus,
        outOfServiceEvents: this._props.outOfServiceEvents,
        vehicleType: this._props.vehicleType,
        registrationNumber: this._props.registrationNumber,
        vehicleStatus: this._props.vehicleStatus,
        vehicleStatusDescription: this._props.vehicleStatusDescription,
        currentCardNumber: this._props.currentCardNumber,
        lastKnownLocationName: this._props.lastKnownLocationName,
        lastKnownCoordinates: this._props.lastKnownCoordinates,
        lastQualifyingPositionReportTime: this._props.lastQualifyingPositionReportTime,
        baseId: this._props.baseId,
        modelName: this._props.modelName,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllExternalSystemUnitEntities = (state: AppState) =>
  state.entityStores.externalSystemUnit.entities;

export const selectExternalSystemUnitById = (id: string) => (state: AppState) =>
  state.entityStores.externalSystemUnit.entities.get(id);

export const selectExternalSystemUnitMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.externalSystemUnit.meta.get(id);

export const selectExternalSystemUnitAndMetadataById = (id: string) =>
  createSelector(
    selectExternalSystemUnitById(id),
    selectExternalSystemUnitMetadataById(id),
    (externalSystemUnit, meta) => ({ externalSystemUnit, meta }),
  );

export function selectExternalSystemUnitEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllExternalSystemUnitEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ExternalSystemUnitRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectExternalSystemUnitEntitiesByName(name: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByUnitStatus(unitStatus: UnitStatus) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.unitStatus === unitStatus).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByVehicleType(vehicleType: VehicleType) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.vehicleType === vehicleType).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByRegistrationNumber(registrationNumber: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.registrationNumber === registrationNumber).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByVehicleStatus(vehicleStatus: VehicleStatus) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.vehicleStatus === vehicleStatus).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByVehicleStatusDescription(
  vehicleStatusDescription: string,
) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.vehicleStatusDescription === vehicleStatusDescription).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByCurrentCardNumber(currentCardNumber: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.currentCardNumber === currentCardNumber).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByLastKnownLocationName(
  lastKnownLocationName: string,
) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.lastKnownLocationName === lastKnownLocationName).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByLastQualifyingPositionReportTime(
  lastQualifyingPositionReportTime: Date,
) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities
      .filter((e) => e.lastQualifyingPositionReportTime === lastQualifyingPositionReportTime)
      .valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectExternalSystemUnitEntitiesByModelName(modelName: string) {
  return createSelector(selectAllExternalSystemUnitEntities, (entities) =>
    entities.filter((e) => e.modelName === modelName).valueSeq(),
  );
}
//#endregion Selectors
